import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Star, Zap, Calendar, BookOpen, Eye, Ear, Users, 
    TrendingUp, Handshake, FileCheck, Rocket, CheckCircle,
    ChevronRight, ChevronLeft, Send, Heart
} from 'lucide-react';
import { useSearchParams, useParams } from 'react-router-dom';
import api from '../../lib/api';

// Badge selection component for 1-click feedback
const BadgeSelector = ({ badges, selected, onSelect, maxSelection = 3 }) => {
    const iconMap = {
        'zap': Zap,
        'calendar-check': Calendar,
        'book-open': BookOpen,
        'eye': Eye,
        'ear': Ear,
        'users': Users,
        'trending-up': TrendingUp,
        'handshake': Handshake,
        'file-check': FileCheck,
        'rocket': Rocket
    };

    const handleToggle = (badgeId) => {
        if (selected.includes(badgeId)) {
            onSelect(selected.filter(id => id !== badgeId));
        } else if (selected.length < maxSelection) {
            onSelect([...selected, badgeId]);
        }
    };

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {badges.map((badge) => {
                const Icon = iconMap[badge.icon] || Star;
                const isSelected = selected.includes(badge.id);
                
                return (
                    <motion.button
                        key={badge.id}
                        onClick={() => handleToggle(badge.id)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                            isSelected 
                                ? 'border-purple-500 bg-purple-500/20' 
                                : 'border-border bg-gray-800/50 hover:border-gray-600'
                        }`}
                        whileTap={{ scale: 0.95 }}
                    >
                        <div className="flex flex-col items-center gap-2">
                            <div className={`p-3 rounded-full ${
                                isSelected ? 'bg-purple-500/30' : 'bg-gray-700'
                            }`}>
                                <Icon size={24} className={isSelected ? 'text-purple-400' : 'text-muted-foreground'} />
                            </div>
                            <span className={`text-sm font-medium ${
                                isSelected ? 'text-purple-300' : 'text-foreground'
                            }`}>
                                {badge.name}
                            </span>
                            {isSelected && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 bg-purple-500 rounded-full p-0.5"
                                >
                                    <CheckCircle size={14} className="text-foreground" />
                                </motion.div>
                            )}
                        </div>
                    </motion.button>
                );
            })}
        </div>
    );
};

// Star rating component
const StarRating = ({ value, onChange, label, description }) => {
    const [hovered, setHovered] = useState(0);

    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center">
                <span className="text-white font-medium">{label}</span>
                <span className="text-muted-foreground text-sm">{value}/5</span>
            </div>
            {description && (
                <p className="text-sm text-muted-foreground">{description}</p>
            )}
            <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                    <motion.button
                        key={star}
                        onClick={() => onChange(star)}
                        onMouseEnter={() => setHovered(star)}
                        onMouseLeave={() => setHovered(0)}
                        whileTap={{ scale: 0.9 }}
                        className="focus:outline-none"
                    >
                        <Star 
                            size={36}
                            className={`transition-colors ${
                                star <= (hovered || value) 
                                    ? 'text-yellow-400 fill-yellow-400' 
                                    : 'text-gray-600'
                            }`}
                        />
                    </motion.button>
                ))}
            </div>
        </div>
    );
};

// Main Seller Feedback Page
const SellerFeedbackPage = () => {
    const [searchParams] = useSearchParams();
    const { type: typeParam } = useParams();
    const token = searchParams.get('token');
    const type = typeParam || searchParams.get('type') || 'closing'; // checkpoint or closing
    
    const [step, setStep] = useState(1);
    const [feedbackData, setFeedbackData] = useState(null);
    const [selectedBadges, setSelectedBadges] = useState([]);
    const [ratings, setRatings] = useState({
        suivi: 0,
        clarte: 0,
        professionnalisme: 0
    });
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadFeedbackData();
    }, [token]);

    const loadFeedbackData = async () => {
        try {
            // Try to fetch real data from API
            if (token) {
                const response = await api.get(`/reputation/feedback-data/${token}`);
                if (response.data) {
                    setFeedbackData(response.data);
                    setLoading(false);
                    return;
                }
            }
            
            // Fallback to demo data for landing page display
            setFeedbackData({
                mandate: {
                    id: 'test-mandate',
                    property_address: '45 Avenue de la République, Lyon',
                    agent_name: 'Sophie Bernard',
                    agent_photo: null,
                    agency_name: 'Immo Excellence'
                },
                checkpoint_type: type === 'checkpoint' ? 'monthly_checkpoint' : 'closing',
                available_badges: [
                    { id: 'reactivite', name: 'Réactivité', icon: 'zap', description: 'Répond rapidement' },
                    { id: 'suivi_regulier', name: 'Suivi régulier', icon: 'calendar-check', description: 'Tient informé régulièrement' },
                    { id: 'pedagogie', name: 'Pédagogie', icon: 'book-open', description: 'Explique clairement' },
                    { id: 'transparence', name: 'Transparence', icon: 'eye', description: 'Honnête sur les difficultés' },
                    { id: 'ecoute', name: 'À l\'écoute', icon: 'ear', description: 'Comprend vos attentes' },
                    { id: 'negociation', name: 'Négociation', icon: 'handshake', description: 'Défend bien vos intérêts' }
                ]
            });
        } catch (err) {
            // Use demo data if API fails
            setFeedbackData({
                mandate: {
                    id: 'test-mandate',
                    property_address: '45 Avenue de la République, Lyon',
                    agent_name: 'Sophie Bernard',
                    agent_photo: null,
                    agency_name: 'Immo Excellence'
                },
                checkpoint_type: type === 'checkpoint' ? 'monthly_checkpoint' : 'closing',
                available_badges: [
                    { id: 'reactivite', name: 'Réactivité', icon: 'zap', description: 'Répond rapidement' },
                    { id: 'suivi_regulier', name: 'Suivi régulier', icon: 'calendar-check', description: 'Tient informé régulièrement' },
                    { id: 'pedagogie', name: 'Pédagogie', icon: 'book-open', description: 'Explique clairement' },
                    { id: 'transparence', name: 'Transparence', icon: 'eye', description: 'Honnête sur les difficultés' },
                    { id: 'ecoute', name: 'À l\'écoute', icon: 'ear', description: 'Comprend vos attentes' },
                    { id: 'negociation', name: 'Négociation', icon: 'handshake', description: 'Défend bien vos intérêts' }
                ]
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            
            if (type === 'closing') {
                await api.post('/reputation/closing-rating', {
                    mandate_id: feedbackData.mandate.id,
                    criteria: ratings,
                    comment,
                    badges: selectedBadges
                });
            } else {
                await api.post('/reputation/checkpoint-feedback', {
                    mandate_id: feedbackData.mandate.id,
                    checkpoint_type: feedbackData.checkpoint_type,
                    badges: selectedBadges
                });
            }
            
            setSubmitted(true);
        } catch (err) {
            setError('Erreur lors de l\'envoi. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    };

    const isClosing = type === 'closing';
    const totalSteps = isClosing ? 3 : 2;

    if (loading && !feedbackData) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="bg-gray-800 rounded-2xl p-8 text-center max-w-md">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">😔</span>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-2">Oups !</h2>
                    <p className="text-muted-foreground">{error}</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <motion.div 
                    className="bg-gray-800 rounded-2xl p-8 text-center max-w-md"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                >
                    <motion.div 
                        className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring" }}
                    >
                        <Heart size={40} className="text-green-400" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-white mb-2">Merci beaucoup !</h2>
                    <p className="text-muted-foreground mb-6">
                        Votre retour aide {feedbackData.mandate.agent_name} à s'améliorer 
                        et aide d'autres vendeurs à faire leur choix.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                        {selectedBadges.map(badgeId => {
                            const badge = feedbackData.available_badges.find(b => b.id === badgeId);
                            return badge && (
                                <span 
                                    key={badgeId}
                                    className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm"
                                >
                                    {badge.name}
                                </span>
                            );
                        })}
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-8 px-4">
            <div className="max-w-lg mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-white mb-2">
                        {isClosing ? 'Évaluez votre agent' : 'Donnez votre avis'}
                    </h1>
                    <p className="text-muted-foreground">
                        {feedbackData.mandate.property_address}
                    </p>
                </div>

                {/* Agent Card */}
                <div className="bg-gray-800/50 rounded-xl p-4 mb-6 flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {feedbackData.mandate.agent_name.charAt(0)}
                    </div>
                    <div>
                        <p className="text-white font-semibold">{feedbackData.mandate.agent_name}</p>
                        <p className="text-muted-foreground text-sm">{feedbackData.mandate.agency_name}</p>
                    </div>
                </div>

                {/* Progress */}
                <div className="flex gap-2 mb-8">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div 
                            key={i}
                            className={`h-1.5 flex-1 rounded-full ${
                                i < step ? 'bg-purple-500' : 'bg-gray-700'
                            }`}
                        />
                    ))}
                </div>

                {/* Steps */}
                <AnimatePresence mode="wait">
                    {/* Step 1: Badge Selection */}
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div className="text-center mb-6">
                                <h2 className="text-lg font-semibold text-white mb-2">
                                    Quelles qualités avez-vous appréciées ?
                                </h2>
                                <p className="text-muted-foreground text-sm">
                                    Sélectionnez jusqu'à 3 qualités (optionnel)
                                </p>
                            </div>

                            <BadgeSelector
                                badges={feedbackData.available_badges}
                                selected={selectedBadges}
                                onSelect={setSelectedBadges}
                                maxSelection={3}
                            />

                            <div className="flex justify-between pt-4">
                                <div></div>
                                <button
                                    onClick={() => setStep(2)}
                                    className="px-6 py-3 bg-purple-500 hover:bg-purple-500 text-white rounded-xl font-medium flex items-center gap-2"
                                >
                                    {isClosing ? 'Continuer' : 'Envoyer'}
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 2: Ratings (Closing only) */}
                    {step === 2 && isClosing && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-8"
                        >
                            <div className="text-center mb-6">
                                <h2 className="text-lg font-semibold text-white mb-2">
                                    Notez votre expérience
                                </h2>
                                <p className="text-muted-foreground text-sm">
                                    Ces notes seront visibles sur le profil de l'agent
                                </p>
                            </div>

                            <div className="space-y-8">
                                <StarRating
                                    label="Suivi"
                                    description="Qualité de l'accompagnement tout au long de la vente"
                                    value={ratings.suivi}
                                    onChange={(v) => setRatings(prev => ({ ...prev, suivi: v }))}
                                />
                                <StarRating
                                    label="Clarté"
                                    description="Transparence et clarté des informations"
                                    value={ratings.clarte}
                                    onChange={(v) => setRatings(prev => ({ ...prev, clarte: v }))}
                                />
                                <StarRating
                                    label="Professionnalisme"
                                    description="Attitude et compétences générales"
                                    value={ratings.professionnalisme}
                                    onChange={(v) => setRatings(prev => ({ ...prev, professionnalisme: v }))}
                                />
                            </div>

                            <div className="flex justify-between pt-4">
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-4 py-2 text-muted-foreground hover:text-white flex items-center gap-2"
                                >
                                    <ChevronLeft size={18} />
                                    Retour
                                </button>
                                <button
                                    onClick={() => setStep(3)}
                                    disabled={!ratings.suivi || !ratings.clarte || !ratings.professionnalisme}
                                    className="px-6 py-3 bg-purple-500 hover:bg-purple-500 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Continuer
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 3: Comment (Closing only) */}
                    {step === 3 && isClosing && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div className="text-center mb-6">
                                <h2 className="text-lg font-semibold text-white mb-2">
                                    Un commentaire ? (optionnel)
                                </h2>
                                <p className="text-muted-foreground text-sm">
                                    Partagez votre expérience pour aider d'autres vendeurs
                                </p>
                            </div>

                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Décrivez votre expérience avec cet agent..."
                                maxLength={800}
                                rows={5}
                                className="w-full bg-gray-800 border border-border rounded-xl p-4 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
                            />
                            <p className="text-xs text-muted-foreground text-right">
                                {comment.length}/800 caractères
                            </p>

                            <div className="flex justify-between pt-4">
                                <button
                                    onClick={() => setStep(2)}
                                    className="px-4 py-2 text-muted-foreground hover:text-white flex items-center gap-2"
                                >
                                    <ChevronLeft size={18} />
                                    Retour
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
                                >
                                    <Send size={18} />
                                    Envoyer mon avis
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Checkpoint: Skip ratings, go directly to submit */}
                    {step === 2 && !isClosing && (
                        <motion.div
                            key="step2-checkpoint"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <div className="bg-gray-800/50 rounded-xl p-6 text-center">
                                <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
                                <h2 className="text-lg font-semibold text-white mb-2">
                                    Prêt à envoyer ?
                                </h2>
                                <p className="text-muted-foreground text-sm mb-4">
                                    {selectedBadges.length > 0 
                                        ? `Vous avez sélectionné ${selectedBadges.length} qualité(s)`
                                        : 'Aucune qualité sélectionnée (vous pouvez continuer)'
                                    }
                                </p>
                                <div className="flex flex-wrap justify-center gap-2">
                                    {selectedBadges.map(badgeId => {
                                        const badge = feedbackData.available_badges.find(b => b.id === badgeId);
                                        return badge && (
                                            <span 
                                                key={badgeId}
                                                className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm"
                                            >
                                                {badge.name}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="flex justify-between pt-4">
                                <button
                                    onClick={() => setStep(1)}
                                    className="px-4 py-2 text-muted-foreground hover:text-white flex items-center gap-2"
                                >
                                    <ChevronLeft size={18} />
                                    Retour
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
                                >
                                    <Send size={18} />
                                    Envoyer
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Footer */}
                <div className="mt-12 text-center">
                    <p className="text-xs text-muted-foreground">
                        Propulsé par <span className="text-purple-400">Homly</span>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SellerFeedbackPage;
